import UserRecoveryNotification from "@Components/common/notifications/user-recovery-notification";
import { AppContext } from "@Context/common/general/context";
import useI18n from "@i18n";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNotificationCandidate } from "../use-notification-candidate";

const RECOVERY_ERROR_KEY = "account.recovery.action.error";

export interface IUseRecoveryNotification {
  actionKey: string;
  hideActionButton?: boolean;
  secondaryActions?: React.ReactNode;
  delayMs?: number;
  id: string;
  isActive: boolean;
  loadingKey: string;
  messageKey: string;
  onRecovered: () => void;
  priority: number;
  recoveryRequest: () => Promise<void>;
  titleKey: string;
}

export const useRecoveryNotification = ({ actionKey, hideActionButton, secondaryActions, delayMs, id, isActive, loadingKey, messageKey, onRecovered, priority, recoveryRequest, titleKey }: IUseRecoveryNotification) => {
  const { dispatch, state } = useContext(AppContext);
  const { i18n } = useI18n();
  const [recoveryErrorKey, setRecoveryErrorKey] = useState<string | null>(null);
  const [isRecovering, setIsRecovering] = useState(false);

  const handleRecoveryResolved = useCallback(() => {
    setRecoveryErrorKey(null);
    onRecovered();
  }, [onRecovered]);

  const handleRecoveryError = useCallback(
    (error: any) => {
      if (error?.code === "functions/failed-precondition") {
        handleRecoveryResolved();
        return;
      }

      setRecoveryErrorKey(RECOVERY_ERROR_KEY);
    },
    [handleRecoveryResolved]
  );

  const handleRecover = useCallback(async () => {
    setRecoveryErrorKey(null);

    try {
      setIsRecovering(true);
      await recoveryRequest();
      handleRecoveryResolved();
    } catch (error) {
      handleRecoveryError(error);
    } finally {
      setIsRecovering(false);
    }
  }, [handleRecoveryError, handleRecoveryResolved, recoveryRequest]);

  useEffect(() => {
    if (!isActive) {
      setRecoveryErrorKey(null);
    }
  }, [isActive]);

  const render = useCallback(
    () => (
      <UserRecoveryNotification
        actionLabel={i18n(actionKey)}
        hideActionButton={hideActionButton}
        secondaryActions={secondaryActions}
        errorMessage={recoveryErrorKey ? i18n(recoveryErrorKey) : null}
        isLoading={isRecovering}
        loadingLabel={i18n(loadingKey)}
        message={i18n(messageKey)}
        onRecover={handleRecover}
        open={isActive}
        title={i18n(titleKey)}
      />
    ),
    [actionKey, handleRecover, i18n, isRecovering, loadingKey, messageKey, recoveryErrorKey, titleKey]
  );

  useNotificationCandidate({
    id,
    priority,
    isActive: state.user.authenticated && isActive,
    delayMs,
    render,
  });
};
